/* Spray assistant UI. Talks to settings.chat_endpoint; without one it runs as a clearly labelled concept preview. */
(() => {
  const root = document.querySelector('[data-chat]');
  if (!root) return;
  const launch = document.querySelector('[data-chat-launch]');
  const log = root.querySelector('[data-chat-log]');
  const form = root.querySelector('[data-chat-form]');
  const input = form.querySelector('input[name="q"]');
  const starters = root.querySelector('[data-chat-starters]');
  const endpoint = root.dataset.endpoint;
  const email = root.dataset.email;
  const history = [];
  const photoInput = root.querySelector('[data-chat-photo]');
  const photoStatus = root.querySelector('[data-chat-photo-status]');
  let photo = null, busy = false, preparing = false;
  const controls = () => {
    form.querySelector('button[type="submit"]').disabled = busy || preparing;
    photoInput.disabled = busy || preparing;
  };
  photoInput.addEventListener('change', async () => {
    photo = null;
    const file = photoInput.files[0];
    if (!file) { photoStatus.textContent = ''; return; }
    preparing = true; controls();
    try {
      if (!file.type.startsWith('image/')) throw new Error('Choose a photo.');
      // Decode locally. No original file or image is uploaded anywhere else.
      const url = URL.createObjectURL(file);
      const img = new Image();
      try {
        await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = url; });
        const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const preview = canvas.toDataURL('image/jpeg', 0.8);
        const data = preview.split(',')[1];
        if (Math.floor(data.length * 3 / 4) > 1.5 * 1024 * 1024) throw new Error('Photo is too large. Try a smaller photo.');
        photo = { preview, media_type: 'image/jpeg', data };
        photoStatus.textContent = 'Photo ready. Send to read the data plate.';
      } finally { URL.revokeObjectURL(url); }
    } catch { photoStatus.textContent = "I couldn't prepare that photo. Try a smaller JPEG, PNG or WebP photo."; }
    finally { preparing = false; controls(); }
  });
  log.addEventListener('click', (e) => {
    // Suggested next questions sit under Tip's latest reply; tapping one sends it.
    const next = e.target.closest('[data-chat-next]');
    if (next && typeof next.textContent === 'string') { send(next.textContent.trim()); return; }
    const button = e.target.closest('[data-chat-add]');
    if (button && window.ASE?.addWithFeedback) {
      window.ASE.addWithFeedback(button, [{ id: Number(button.dataset.chatAdd), quantity: 1 }]);
    }
  });

  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const lite = (s) => esc(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[(.+?)\]\((\/[^)\s]*|https:\/\/[^)\s]+)\)/g, '<a href="$2">$1</a>')
    .split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');

  const open = (prefill) => {
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    launch.setAttribute('aria-expanded', 'true');
    if (prefill) { input.value = prefill; }
    setTimeout(() => input.focus(), 200);
  };
  const close = () => {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    launch.setAttribute('aria-expanded', 'false');
  };
  window.ASE = Object.assign(window.ASE || {}, { chat: { open } });
  // Email links use ?tip=open to land with Tip already open.
  if (new URLSearchParams(location.search).get('tip') === 'open') open();

  launch.addEventListener('click', () => (root.classList.contains('is-open') ? close() : open()));
  root.querySelector('[data-chat-close]').addEventListener('click', close);
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-chat-open]');
    if (t) { e.preventDefault(); open(t.dataset.chatOpen); }
  });
  starters.addEventListener('click', (e) => {
    const b = e.target.closest('[data-chat-starter]');
    if (b) send(b.textContent.trim());
  });
  // Typing your own question means the presets are no longer needed.
  input.addEventListener('input', () => { if (input.value.trim()) starters.hidden = true; });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const q = input.value.trim();
    if (q || photo) send(q || 'Please read this data plate.');
  });

  const add = (role, html) => {
    const d = document.createElement('div');
    d.className = `msg msg--${role}`;
    d.innerHTML = html;
    log.appendChild(d);
    log.scrollTop = log.scrollHeight;
    return d;
  };

  let nextRow = null;
  async function send(raw) {
    if (busy || preparing) return;
    const q = String(raw).trim().slice(0, 600);
    if (!q) return;
    busy = true; controls();
    const attached = photo;
    photo = null; photoInput.value = ''; photoStatus.textContent = '';
    starters.hidden = true;
    if (nextRow) { nextRow.remove(); nextRow = null; }
    input.value = '';
    add('me', `<p>${esc(q)}</p>${attached ? `<img src="${attached.preview}" alt="Data plate photo" width="120" style="max-height:120px;object-fit:contain">` : ''}`);
    history.push({ role: 'user', content: q });
    const typing = add('bot', '<span class="msg--typing"><i></i><i></i><i></i></span>');

    if (!endpoint) {
      typing.remove();
      const mail = `mailto:${email}?subject=${encodeURIComponent('Question from the website')}&body=${encodeURIComponent(q + (root.dataset.productTitle ? `\n\nMachine: ${root.dataset.productTitle}` : ''))}`;
      add('bot', `<p>This is a preview of the spray assistant. It isn't connected yet, so I can't answer live.</p><p><a href="${mail}">Email this question</a> and a person will reply.</p>`);
      busy = false; controls();
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 25000);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({ messages: history.slice(-12), context: { page: location.pathname, product_handle: root.dataset.product || null }, ...(attached ? { image: { media_type: attached.media_type, data: attached.data } } : {}) })
      });
      const data = await res.json();
      if (!data.reply || (!res.ok && !data.handoff)) throw new Error(String(res.status));
      typing.remove();
      history.push({ role: 'assistant', content: data.reply });
      const money = (n) => Number.isFinite(n) ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n) : '';
      const cards = (Array.isArray(data.products) ? data.products : []).slice(0, 4).map((p) => {
        const href = `/products/${encodeURIComponent(p.handle)}${p.variant ? `?variant=${encodeURIComponent(p.variant)}` : ''}`;
        const action = p.variant && /^\d+$/.test(p.variant)
          ? `<button type="button" class="btn btn--sm btn--line" data-chat-add="${esc(p.variant)}">Add to cart</button>`
          : `<a class="btn btn--sm btn--line" href="${esc(href)}">View</a>`;
        const hasImage = typeof p.image === 'string' && /^(https:\/\/|\/\/)/.test(p.image);
        const image = hasImage ? `<img src="${esc(p.image)}" alt="" width="52" height="52" loading="lazy">` : '';
        return `<div class="chat__card${hasImage ? ' chat__card--img' : ''}">${image}<span><a href="${esc(href)}"><strong>${esc(p.title)}</strong></a><br>${esc(money(p.price))}${p.stock ? `<span class="muted">${esc(p.stock)}</span>` : ''}</span>${action}</div>`;
      }).join('');
      let handoff = '';
      if (data.handoff) {
        const transcript = history.map((m) => `${m.role === 'user' ? 'Customer' : 'Tip'}: ${m.content}`).join('\n\n');
        const body = `${data.handoff.summary}\n\nTranscript\n${transcript}${attached ? '\n\nA data plate photo was shared with Tip. Please attach it to your email reply if needed.' : ''}`;
        const href = `/pages/contact?${new URLSearchParams({ 'contact[body]': body })}`;
        // The summary is written for staff: it travels in the contact form body, never shown to the customer.
        handoff = `<p><a class="btn btn--sm btn--line" href="${esc(href)}">Send this to the team</a></p><p>Or email <a href="mailto:spray@airlessspray.com.au">spray@airlessspray.com.au</a>.</p>`;
      }
      const bubble = add('bot', lite(data.reply || '') + cards + handoff);
      const next = (Array.isArray(data.followups) ? data.followups : []).filter((s) => typeof s === 'string' && s.trim()).slice(0, 3);
      if (next.length) {
        const row = document.createElement('div');
        row.className = 'chat__next';
        row.innerHTML = next.map((s) => `<button type="button" class="chip" data-chat-next>${esc(s.trim())}</button>`).join('');
        log.appendChild(row);
        nextRow = row;
      }
      // Start the reader at the top of a long answer rather than its end.
      log.scrollTop = Math.max(0, bubble.offsetTop - 8);
    } catch (err) {
      typing.remove();
      add('bot', `<p>I couldn't reach the assistant just now. Email <a href="mailto:${esc(email)}">${esc(email)}</a> and a person will reply.</p>`);
    } finally { clearTimeout(timer); busy = false; controls(); }
  }
})();
